import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  Stethoscope, Users, Clock, AlertCircle, CheckCircle, 
  UserPlus, Copy, RefreshCw, Search, ChevronRight,
  Activity, Pill, Brain, FileText, Lock, X, Eye,
  TrendingUp, TrendingDown, Minus, Calendar, Heart, ChevronDown, BookOpen, Video
} from 'lucide-react';

// Mood emoji mapping
const MOOD_LABELS = {
  peaceful: '😌 Peaceful',
  anxious: '😰 Anxious',
  sad: '😢 Sad',
  angry: '😠 Angry',
  hopeful: '🌟 Hopeful',
  confused: '😕 Confused'
};

// Vault Entry Card Component - Expandable to view full details
const VaultEntryCard = ({ entry }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMoodDisplay = (mood) => {
    return MOOD_LABELS[mood] || mood;
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header - Always Visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-semibold text-foreground text-lg mb-1">
              {entry.title || 'Untitled Entry'}
            </h4>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(entry.created_at)}
              </span>
              {entry.mood && (
                <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded-full text-xs">
                  {getMoodDisplay(entry.mood)}
                </span>
              )}
            </div>
          </div>
          <button className="p-1.5 hover:bg-muted rounded-lg transition-colors">
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
        
        {/* Preview - First 150 characters when collapsed */}
        {!isExpanded && entry.content && (
          <p className="text-muted-foreground text-sm mt-2 line-clamp-2">
            {entry.content.substring(0, 150)}{entry.content.length > 150 ? '...' : ''}
          </p>
        )}
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="pt-4">
            {/* Full Content */}
            <div className="bg-muted/30 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-violet-400" />
                <h5 className="font-medium text-foreground">Journal Entry</h5>
              </div>
              <p className="text-foreground whitespace-pre-wrap leading-relaxed">
                {entry.content || 'No content available'}
              </p>
            </div>
            
            {/* Tags */}
            {entry.tags && entry.tags.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-muted-foreground mb-2">Tags</h5>
                <div className="flex flex-wrap gap-2">
                  {entry.tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Images */}
            {entry.images && entry.images.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-muted-foreground mb-2">Attached Images</h5>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {entry.images.map((img, i) => (
                    <img 
                      key={i} 
                      src={img} 
                      alt={`Attachment ${i + 1}`}
                      className="rounded-lg w-full h-32 object-cover border border-border"
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Metadata */}
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <span>Entry ID: {entry.id?.substring(0, 8) || 'N/A'}</span>
              {entry.updated_at && entry.updated_at !== entry.created_at && (
                <span>Last updated: {new Date(entry.updated_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Patient Card Component
const PatientCard = ({ patient, onViewDetails }) => {
  const navigate = useNavigate();
  
  const getMoodColor = (mood) => {
    if (!mood) return 'bg-muted text-muted-foreground';
    if (mood >= 4) return 'bg-green-500/20 text-green-400';
    if (mood >= 3) return 'bg-yellow-500/20 text-yellow-400';
    return 'bg-red-500/20 text-red-400';
  };

  const handleVideoCall = async (e) => {
    e.stopPropagation();
    try {
      const response = await api.post('/video/create-room', {
        target_user_id: patient.patient_id
      });
      navigate(`/video/${response.data.room_id}?target=${patient.patient_id}&name=${encodeURIComponent(patient.patient_name)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start video call');
    }
  };

  return (
    <div 
      className="bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-all cursor-pointer"
      onClick={() => onViewDetails(patient)}
      data-testid={`patient-card-${patient.patient_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-foreground">{patient.patient_name}</h3>
          <p className="text-sm text-muted-foreground">{patient.patient_email}</p>
        </div>
        <div className={`px-2 py-1 rounded-full text-xs ${getMoodColor(patient.last_mood)}`}>
          {patient.last_mood ? `Mood: ${patient.last_mood}/5` : 'No mood data'}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Connected: {new Date(patient.connected_since).toLocaleDateString()}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleVideoCall}
            className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
            title="Start Video Call"
            data-testid={`video-call-btn-${patient.patient_id}`}
          >
            <Video className="w-4 h-4" />
          </button>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
};

// Patient Detail Modal
const PatientDetailModal = ({ patient, onClose }) => {
  const [activeTab, setActiveTab] = useState('mood');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [vaultPin, setVaultPin] = useState('');
  const [vaultAccess, setVaultAccess] = useState(null);
  const [vaultLoading, setVaultLoading] = useState(false);

  const fetchData = useCallback(async (tab) => {
    setLoading(true);
    try {
      let response;
      switch (tab) {
        case 'mood':
          response = await api.get(`/doctor-portal/doctor/patients/${patient.patient_id}/mood?days=30`);
          break;
        case 'medications':
          response = await api.get(`/doctor-portal/doctor/patients/${patient.patient_id}/medications`);
          break;
        case 'mindpulse':
          response = await api.get(`/doctor-portal/doctor/patients/${patient.patient_id}/mindpulse`);
          break;
        case 'summary':
          response = await api.get(`/doctor-portal/doctor/patients/${patient.patient_id}/summary`);
          break;
        default:
          break;
      }
      setData(response?.data);
    } catch (error) {
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [patient.patient_id]);

  useEffect(() => {
    if (activeTab !== 'vault') {
      fetchData(activeTab);
    }
  }, [activeTab, fetchData]);

  const handleVaultAccess = async () => {
    if (!vaultPin || vaultPin.length < 4) {
      toast.error('Please enter a valid PIN');
      return;
    }
    setVaultLoading(true);
    try {
      const response = await api.post(`/doctor-portal/doctor/patients/${patient.patient_id}/vault/access`, {
        patient_id: patient.patient_id,
        doctor_access_pin: vaultPin
      });
      setVaultAccess(response.data);
      toast.success('Vault access granted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired PIN');
    } finally {
      setVaultLoading(false);
    }
  };

  const tabs = [
    { id: 'mood', label: 'Mood Journal', icon: Heart },
    { id: 'medications', label: 'Medications', icon: Pill },
    { id: 'mindpulse', label: 'MindPulse', icon: Brain },
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'vault', label: 'Private Vault', icon: Lock },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground">{patient.patient_name}</h2>
            <p className="text-sm text-muted-foreground">{patient.patient_email}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Mood Tab */}
              {activeTab === 'mood' && data && (
                <div className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-muted/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">{data.average_mood || '-'}</p>
                      <p className="text-xs text-muted-foreground">Avg Mood</p>
                    </div>
                    <div className="bg-muted/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">{data.total}</p>
                      <p className="text-xs text-muted-foreground">Entries</p>
                    </div>
                    <div className="bg-muted/50 rounded-xl p-4 text-center flex flex-col items-center justify-center">
                      {data.mood_trend === 'improving' && <TrendingUp className="w-6 h-6 text-green-400" />}
                      {data.mood_trend === 'declining' && <TrendingDown className="w-6 h-6 text-red-400" />}
                      {data.mood_trend === 'stable' && <Minus className="w-6 h-6 text-yellow-400" />}
                      <p className="text-xs text-muted-foreground capitalize">{data.mood_trend}</p>
                    </div>
                  </div>
                  
                  {/* Entries */}
                  <div className="space-y-2">
                    <h3 className="font-medium text-foreground">Recent Entries</h3>
                    {data.entries?.slice(0, 10).map((entry, idx) => (
                      <div key={idx} className="bg-muted/30 rounded-lg p-3 flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              entry.mood_scale >= 4 ? 'bg-green-500 text-white' :
                              entry.mood_scale >= 3 ? 'bg-yellow-500 text-white' :
                              'bg-red-500 text-white'
                            }`}>
                              {entry.mood_scale}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {new Date(entry.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {entry.note && <p className="text-sm text-foreground">{entry.note}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Medications Tab */}
              {activeTab === 'medications' && data && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">{data.total_medications}</p>
                      <p className="text-xs text-muted-foreground">Active Meds</p>
                    </div>
                    <div className="bg-muted/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-foreground">{data.adherence_rate || '-'}%</p>
                      <p className="text-xs text-muted-foreground">Adherence</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-foreground">Medications</h3>
                    {data.medications?.map((med, idx) => (
                      <div key={idx} className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground">{med.name}</p>
                            <p className="text-sm text-muted-foreground">{med.dosage} - {med.frequency}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs ${med.is_active ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>
                            {med.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MindPulse Tab */}
              {activeTab === 'mindpulse' && data && (
                <div className="space-y-4">
                  {data.wellness_status && (
                    <div className={`p-4 rounded-xl border ${
                      data.wellness_status.risk_level === 'high' ? 'bg-red-500/10 border-red-500/30' :
                      data.wellness_status.risk_level === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      'bg-green-500/10 border-green-500/30'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-5 h-5" />
                        <span className="font-medium capitalize">{data.wellness_status.risk_level} Risk Level</span>
                      </div>
                      {data.wellness_status.message && (
                        <p className="text-sm text-muted-foreground">{data.wellness_status.message}</p>
                      )}
                    </div>
                  )}
                  
                  {data.latest_analysis && (
                    <div className="bg-muted/30 rounded-xl p-4">
                      <h3 className="font-medium text-foreground mb-2">Latest AI Analysis</h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {data.latest_analysis.analysis || data.latest_analysis.summary || 'No analysis available'}
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-foreground">Recent Signals</h3>
                    {data.signals?.slice(0, 5).map((signal, idx) => (
                      <div key={idx} className="bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-foreground capitalize">{signal.signal_type}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(signal.recorded_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary Tab */}
              {activeTab === 'summary' && data && (
                <div className="space-y-4">
                  <div className="bg-muted/30 rounded-xl p-4">
                    <h3 className="font-medium text-foreground mb-3">Patient Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Name</p>
                        <p className="text-foreground">{data.patient?.name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Age</p>
                        <p className="text-foreground">{data.patient?.age}</p>
                      </div>
                    </div>
                  </div>
                  
                  {data.mood_stats && (
                    <div className="bg-muted/30 rounded-xl p-4">
                      <h3 className="font-medium text-foreground mb-3">Mood Statistics (30 days)</h3>
                      <div className="grid grid-cols-4 gap-4 text-sm text-center">
                        <div>
                          <p className="text-2xl font-bold text-foreground">{data.mood_stats.average}</p>
                          <p className="text-muted-foreground">Average</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-foreground">{data.mood_stats.lowest}</p>
                          <p className="text-muted-foreground">Lowest</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-foreground">{data.mood_stats.highest}</p>
                          <p className="text-muted-foreground">Highest</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-foreground">{data.mood_stats.entries_count}</p>
                          <p className="text-muted-foreground">Entries</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {data.medications?.length > 0 && (
                    <div className="bg-muted/30 rounded-xl p-4">
                      <h3 className="font-medium text-foreground mb-3">Active Medications</h3>
                      <ul className="space-y-1 text-sm">
                        {data.medications.map((med, idx) => (
                          <li key={idx} className="text-muted-foreground">
                            • {med.name} ({med.dosage})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Vault Tab */}
              {activeTab === 'vault' && (
                <div className="space-y-4">
                  {!vaultAccess ? (
                    <div className="text-center py-8">
                      <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">Private Vault Access</h3>
                      <p className="text-sm text-muted-foreground mb-6">
                        Enter the doctor access PIN shared by your patient to view their private vault entries.
                      </p>
                      <div className="max-w-xs mx-auto space-y-4">
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="Enter 6-digit PIN"
                          className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground text-center text-2xl tracking-widest"
                          value={vaultPin}
                          onChange={(e) => setVaultPin(e.target.value.replace(/\D/g, ''))}
                        />
                        <button
                          onClick={handleVaultAccess}
                          disabled={vaultLoading || vaultPin.length < 4}
                          className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
                        >
                          {vaultLoading ? 'Verifying...' : 'Access Vault'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="font-medium text-green-400">Vault Access Granted</p>
                          <p className="text-xs text-muted-foreground">Viewing {vaultAccess.total} private journal entries</p>
                        </div>
                      </div>
                      
                      {vaultAccess.entries?.length === 0 ? (
                        <div className="text-center py-8">
                          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground">No vault entries found</p>
                        </div>
                      ) : (
                        vaultAccess.entries?.map((entry, idx) => (
                          <VaultEntryCard key={entry.id || idx} entry={entry} />
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Connection Code Modal
const ConnectionCodeModal = ({ code, onClose }) => {
  const copyCode = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code.code);
        toast.success('Code copied to clipboard!');
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = code.code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Code copied to clipboard!');
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy. Please copy manually: ' + code.code);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md text-center">
        <h2 className="text-xl font-bold text-foreground mb-2">Your Connection Code</h2>
        <p className="text-sm text-muted-foreground mb-6">Share this code with your patients</p>
        
        <div 
          className="bg-muted rounded-xl p-4 mb-4 cursor-pointer hover:bg-muted/70 transition-colors"
          onClick={copyCode}
        >
          <p className="text-3xl font-mono font-bold text-primary tracking-widest">{code.code}</p>
          <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
            <Copy className="w-3 h-3" /> Click to copy
          </p>
        </div>
        
        <p className="text-xs text-muted-foreground mb-4">
          Expires: {new Date(code.expires_at).toLocaleString()}
        </p>
        
        <button
          onClick={onClose}
          className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:opacity-90"
        >
          Done
        </button>
      </div>
    </div>
  );
};

// Main Doctor Dashboard Component
const DoctorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [pendingConnections, setPendingConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const isApproved = user?.doctor_status === 'approved';
  const isPending = user?.doctor_status === 'pending_approval';

  useEffect(() => {
    if (user?.user_type !== 'doctor') {
      navigate('/dashboard');
      return;
    }
    if (isApproved) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user, isApproved, navigate]);

  const fetchData = async () => {
    try {
      const [patientsRes, pendingRes] = await Promise.all([
        api.get('/doctor-portal/doctor/patients'),
        api.get('/doctor-portal/connections/pending')
      ]);
      setPatients(patientsRes.data.patients || []);
      setPendingConnections(pendingRes.data.connections || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    try {
      const response = await api.post('/doctor-portal/doctor/generate-code', { expires_in_hours: 24 });
      setGeneratedCode(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate code');
    }
  };

  const respondToConnection = async (connectionId, approve) => {
    try {
      await api.post('/doctor-portal/connections/respond', { connection_id: connectionId, approve });
      toast.success(approve ? 'Connection approved!' : 'Connection rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to respond');
    }
  };

  const filteredPatients = patients.filter(p => 
    p.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.patient_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pending Approval View
  if (isPending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-yellow-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Account Pending Approval</h1>
          <p className="text-muted-foreground mb-6">
            Your doctor account is currently under review by our admin team. 
            You'll receive an email once your account is approved.
          </p>
          <div className="bg-muted/50 rounded-xl p-4 text-left text-sm">
            <p className="text-muted-foreground mb-2">Submitted Information:</p>
            <p className="text-foreground">License: {user?.medical_license || 'N/A'}</p>
            <p className="text-foreground">Specialization: {user?.specialization || 'N/A'}</p>
            <p className="text-foreground">Hospital: {user?.hospital_affiliation || 'N/A'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Rejected/Suspended View
  if (user?.doctor_status === 'rejected' || user?.doctor_status === 'suspended') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Account {user?.doctor_status === 'rejected' ? 'Not Approved' : 'Suspended'}
          </h1>
          <p className="text-muted-foreground">
            {user?.doctor_status === 'rejected' 
              ? 'Your doctor application was not approved. Please contact support for more information.'
              : 'Your doctor account has been suspended. Please contact support.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="doctor-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Stethoscope className="w-8 h-8 text-cyan-400" />
              Doctor's Portal
            </h1>
            <p className="text-muted-foreground mt-1">
              Welcome, Dr. {user?.full_name}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={generateCode}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-xl hover:bg-cyan-500/30 transition-colors"
            >
              <UserPlus className="w-5 h-5" />
              Generate Code
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{patients.length}</p>
                <p className="text-sm text-muted-foreground">Connected Patients</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{pendingConnections.length}</p>
                <p className="text-sm text-muted-foreground">Pending Requests</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {patients.filter(p => p.last_mood >= 4).length}
                </p>
                <p className="text-sm text-muted-foreground">Patients Doing Well</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Connection Requests */}
        {pendingConnections.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Pending Connection Requests
            </h2>
            <div className="space-y-3">
              {pendingConnections.map((conn) => (
                <div key={conn.id} className="bg-card rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground">{conn.patient_name}</p>
                    <p className="text-sm text-muted-foreground">{conn.patient_email}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => respondToConnection(conn.id, true)}
                      className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => respondToConnection(conn.id, false)}
                      className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Patients List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">My Patients</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search patients..."
                className="pl-10 pr-4 py-2 bg-muted rounded-xl text-foreground text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Patients Yet</h3>
              <p className="text-muted-foreground mb-4">
                Generate a code or enter a patient's code to connect
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPatients.map((patient) => (
                <PatientCard
                  key={patient.patient_id}
                  patient={patient}
                  onViewDetails={setSelectedPatient}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
        />
      )}

      {generatedCode && (
        <ConnectionCodeModal
          code={generatedCode}
          onClose={() => setGeneratedCode(null)}
        />
      )}
    </div>
  );
};

export default DoctorDashboard;
